import { sendEvent } from '@onyx/observability-v2/client';

export type ErrorType = 'user' | 'system' | 'network';

type ErrorEventContext = {
  endpoint?: string;
  fieldName?: string;
  statusCode?: number;
  step?: string;
  extra?: Record<string, unknown>;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Unexpected error occurred';
};

const getStepFromPath = (path: string): string => {
  if (path.includes('/issue/create/area')) {
    return 'area_selection';
  }
  if (path.includes('/issue/create/department')) {
    return 'department_selection';
  }
  if (path.includes('/issue/create/category')) {
    return 'category_selection';
  }
  if (path.includes('/issue/create/immediate-action')) {
    return 'immediate_action';
  }
  if (path.includes('/issue/create/escalation')) {
    return 'escalation';
  }
  if (path.includes('/issue/create/summary')) {
    return 'summary';
  }
  if (path.includes('/issue/create/submitted')) {
    return 'submitted';
  }

  return path;
};

export const trackFormValidationError = ({
  errorMessage,
  fieldName,
  step,
  extra,
}: {
  errorMessage: string;
  fieldName: string;
  step: string;
  extra?: Record<string, unknown>;
}) => {
  sendEvent('help-hub.form_validation_error', {
    'error.code': `VALIDATION_${step.toUpperCase()}`,
    'error.message': errorMessage,
    'error.type': 'user',
    'error.field': fieldName,
    'error.step': step,
    ...extra,
  });
};

export const trackApiError = (
  error: unknown,
  {
    endpoint,
    statusCode,
    step,
    extra,
  }: Pick<ErrorEventContext, 'endpoint' | 'statusCode' | 'step' | 'extra'> = {}
) => {
  sendEvent('help-hub.issue.create.error.api', {
    'error.code': statusCode ? `HTTP_${statusCode}` : 'NETWORK_ERROR',
    'error.message': getErrorMessage(error),
    'error.type': statusCode ? 'system' : 'network',
    'error.endpoint': endpoint,
    'error.status_code': statusCode,
    'error.step': step,
    ...extra,
  });
};

export const trackStepError = ({
  step,
  errorMessage,
  errorType = 'system',
  errorCode,
  extra,
}: {
  step: string;
  errorMessage: string;
  errorType?: ErrorType;
  errorCode?: string;
  extra?: Record<string, unknown>;
}) => {
  sendEvent('help-hub.issue.create.error.step', {
    'error.code': errorCode ?? step.toUpperCase(),
    'error.message': errorMessage,
    'error.type': errorType,
    'error.step': step,
    ...extra,
  });
};

export const trackUploadError = (
  error: unknown,
  context?: Omit<ErrorEventContext, 'fieldName'>
) => {
  trackStepError({
    step: context?.step ?? 'attachments',
    errorMessage: getErrorMessage(error),
    errorType: 'user',
    errorCode: context?.statusCode
      ? `UPLOAD_HTTP_${context.statusCode}`
      : 'UPLOAD_FAILED',
    extra: {
      'error.endpoint': context?.endpoint,
      'error.status_code': context?.statusCode,
      ...(context?.extra ?? {}),
    },
  });

  sendEvent('help-hub.issue.create.error.upload', {
    'error.code': context?.statusCode
      ? `UPLOAD_HTTP_${context.statusCode}`
      : 'UPLOAD_FAILED',
    'error.message': getErrorMessage(error),
    'error.type': 'user',
    'error.endpoint': context?.endpoint,
    'error.status_code': context?.statusCode,
    'error.step': context?.step ?? 'attachments',
    ...(context?.extra ?? {}),
  });
};

export const trackScannerError = (
  error: unknown,
  context?: Omit<ErrorEventContext, 'fieldName'>
) => {
  sendEvent('help-hub.issue.create.error.scanner', {
    'error.code': context?.statusCode
      ? `SCANNER_HTTP_${context.statusCode}`
      : 'SCANNER_FAILED',
    'error.message': getErrorMessage(error),
    'error.type': 'system',
    'error.endpoint': context?.endpoint,
    'error.status_code': context?.statusCode,
    'error.step': context?.step ?? 'barcode_scanner',
    ...(context?.extra ?? {}),
  });
};

export const trackBackNavigation = ({
  fromPath,
  toPath,
}: {
  fromPath: string;
  toPath: string;
}) => {
  sendEvent('help-hub.navigation.back', {
    'navigation.type': 'back_navigation',
    'navigation.from_step': getStepFromPath(fromPath),
    'navigation.to_step': getStepFromPath(toPath),
  });
};

export const trackBrowserBack = ({
  fromPath,
  toPath,
}: {
  fromPath: string;
  toPath: string;
}) => {
  sendEvent('help-hub.navigation.browser-back', {
    'navigation.type': 'browser_back',
    'navigation.from_step': getStepFromPath(fromPath),
    'navigation.to_step': getStepFromPath(toPath),
  });
};
